// ModalRoot — App.tsx 최상단에 1회 mount. modalStore.active 가 있으면 dialog 렌더.
// 크크 가이드라인 #1: createPortal + fade-in/scale-in 애니메이션 (tokens.css).
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useModalStore } from '../stores/modalStore';
import { useEscapeClose } from '../hooks/useEscapeClose';

export default function ModalRoot() {
	const { t } = useTranslation();
	const active = useModalStore((s) => s.active);
	const close = useModalStore((s) => s.close);
	const [input, setInput] = useState('');
	// input(한 줄) / textarea(multiline) 공용 — focus 만 쓰므로 union ref
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

	useEffect(() => {
		if (!active) return;
		setInput(active.defaultValue || '');
		if (active.kind === 'prompt') setTimeout(() => inputRef.current?.focus(), 30);
	}, [active]);

	// ESC + 안드로이드 하드웨어 뒤로가기 = 취소 (뒤로가기는 2026-08-07 추가).
	//   훅은 early return 위에서 불러야 하므로 cancel 정의도 위로 (active 는 호출 시점에 재확인).
	const cancel = () => {
		if (!active) return;
		if (active.kind === 'alert') close(undefined);
		else if (active.kind === 'confirm') close(false);
		else if (active.kind === 'prompt') close(null);
	};
	useEscapeClose(cancel, !!active);

	if (!active) return null;

	const ok = () => {
		if (active.kind === 'alert') close(undefined);
		else if (active.kind === 'confirm') close(true);
		else if (active.kind === 'prompt') close(input);
	};

	const okLabel = active.okLabel || t('dialog.ok');
	const cancelLabel = active.cancelLabel || t('dialog.cancel');
	const okBg = active.danger ? 'bg-red-500 hover:bg-red-600' : 'bg-class-blue hover:brightness-95';

	const portalTarget = typeof document !== 'undefined' ? document.body : null;
	const content = (
		<div role="dialog" aria-modal="true" data-testid={`tpt-modal-${active.kind}`}
			className="tpt-dialog-backdrop fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4">
			<div onClick={(e) => e.stopPropagation()}
				className={`tpt-dialog-content rounded-2xl bg-surface shadow-2xl ${active.multiline ? 'w-[min(560px,94vw)]' : 'w-[min(420px,94vw)]'}`}>
				{active.title && (
					<div className="border-b border-line-subtle px-5 py-3 text-ui-base font-bold text-fg">{active.title}</div>
				)}
				<div className="whitespace-pre-wrap px-5 py-4 text-ui-sm text-fg" data-testid="tpt-modal-message"
					style={{ fontSize: 'var(--tpt-font-size-sm, 13px)' }}>
					{active.message}
				</div>
				{active.kind === 'prompt' && (
					<div className="px-5 pb-3">
						{active.multiline ? (
							// 📝 여러 줄 입력 (공지사항 등) — Enter 는 줄바꿈, Ctrl/⌘+Enter 가 확인.
							//   한 줄 input 처럼 Enter 로 확정하면 줄바꿈을 아예 못 넣으므로 키 규칙을 분리한다.
							<>
								<textarea ref={(el) => { inputRef.current = el; }} value={input} onChange={(e) => setInput(e.target.value)}
									onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); ok(); } }}
									rows={active.rows || 5}
									placeholder={active.placeholder || ''}
									data-testid="tpt-modal-input"
									className="w-full resize-y rounded-md border border-line bg-surface-subtle px-3 py-2 text-ui-sm leading-relaxed focus:border-class-blue focus:outline-none" />
								<div className="mt-1 text-ui-xs text-fg-faint" data-testid="tpt-modal-multiline-hint">{t('dialog.multilineHint')}</div>
							</>
						) : (
							<input ref={(el) => { inputRef.current = el; }} type="text" value={input} onChange={(e) => setInput(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ok(); } }}
								placeholder={active.placeholder || ''}
								data-testid="tpt-modal-input"
								className="w-full rounded-md border border-line bg-surface-subtle px-3 py-2 text-ui-sm focus:border-class-blue focus:outline-none" />
						)}
					</div>
				)}
				<div className="flex justify-end gap-2 border-t border-line-subtle px-5 py-3">
					{active.kind !== 'alert' && (
						<button type="button" onClick={cancel}
							data-testid="tpt-modal-cancel"
							className="rounded-md bg-surface-muted px-4 py-1.5 text-ui-sm font-semibold text-fg hover:bg-hover-strong">
							{cancelLabel}
						</button>
					)}
					<button type="button" onClick={ok}
						data-testid="tpt-modal-ok"
						className={`rounded-md px-4 py-1.5 text-ui-sm font-semibold text-white ${okBg}`}>
						{okLabel}
					</button>
				</div>
			</div>
		</div>
	);
	return portalTarget ? createPortal(content, portalTarget) : content;
}
